/**
 * Copyright (c) 2022 Schwäbische Werkzeugmaschinen GmbH
 *
 * @author Sebastian Roming
 * @since 5.0.0
 */

import { html, LitElement, PropertyValues, TemplateResult }                 from 'lit';
import { customElement, state }                                             from 'lit/decorators.js';
import { showToast }                                                        from '../../items/show-toast';
import { LdLoginStyles }                                                    from './styles';
import { PaperToastElement }                                                from '@polymer/paper-toast';
import { baseConfig }                                                       from '../../auto-generated/config';
import { PaperDialogElement }                                               from '@polymer/paper-dialog';
import { isAllowedBrowser, isInMaintenanceMode, isMaintenanceAnnouncement } from '../../items/globals';
import '@polymer/paper-input/paper-input';
import '@polymer/paper-button/paper-button';
import '@polymer/paper-toast/paper-toast';
import '@polymer/paper-dialog/paper-dialog';
import '@polymer/paper-dialog-scrollable/paper-dialog-scrollable';
import '@lifedata/ld-markdown-element/ld-markdown-element.js';


@customElement('ld-login-application')
export class LdLoginApplication extends LitElement {

  @state() private _privacyPolicyUrl: string = baseConfig.privacyPolicy.baseUrl + baseConfig.privacyPolicy.documentUrls.en;

  static styles = LdLoginStyles;

  // --------------------------------------------------------------------------------------
  constructor() {
    super();
    this.addEventListener('event', (e: CustomEventInit) => {
      if (e.detail.eventType === 'request-url-params') {
        const queryString  = window.location.search;
        e.detail.processed = true;
        e.detail.value     = new Map(Array.from(new URLSearchParams(queryString).entries()));
      } else if (e.detail.eventType === 'notification-requested') {
        showToast(e.detail.message)
      }
    });
    this.setupListnerForTokenRequest();
  }

  // --------------------------------------------------------------------------------------
  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    isAllowedBrowser();

    const queryString = window.location.search;
    const urlParams   = new URLSearchParams(queryString);

    if (urlParams.has('message')) {
      const message = urlParams.get('message');
      showToast(message!);
    }

    if (urlParams.has('logout')) {
      this.clearToken();
    }
  }

  clearToken() {
    const tokenKey = baseConfig.localstorage.token_key;
    localStorage.removeItem(tokenKey);
  }

  // --------------------------------------------------------------------------------------
  render(): TemplateResult {

    let _maintenanceAnnouncement = [];
    if (isMaintenanceAnnouncement()) {
      _maintenanceAnnouncement.push(html`
        <section class='announcement maintenance-announcement'><p>Announcement</p>
          ${baseConfig.maintenanceAnnouncement.text}
        </section>`);
    }

    if (isInMaintenanceMode()) {
      _maintenanceAnnouncement = [];
      _maintenanceAnnouncement.push(html`
        <section class='announcement maintenance-announcement'><p>Maintenance</p>${baseConfig.maintenanceMode.text}
        </section>`);
    }

    return html`

      <paper-toast id="ld-login-toast" duration="0" class="fit-bottom">
        <span class="ld-login-toast-message">This is a test. This is a test. This is a test.</span>
        <paper-button @click="${() => this._onLoginToastCloseClick()}">Close</paper-button>
      </paper-toast>

      <paper-dialog id="privacyPolicyDialog" no-cancel-on-esc-key="true" .modal="${true}"
                    no-cancel-on-outside-click="true">
        <h2>Privacy Policy</h2>
        <div class="privacy-language"><a @click="${(e: Event) => this._onPrivacyLanguageChange(e)}" data-lng="en"
                                         href="javascript:;">ENGLISH VERSION</a> | <a
          @click="${(e: Event) => this._onPrivacyLanguageChange(e)}" data-lng="de" href="javascript:;">DEUTSCHE
          VERSION</a></div>
        <paper-dialog-scrollable id="scrollabledialog">
          <ld-markdown-element>
            <div slot="markdown-html"></div>
            <script
              slot="markdown-src"
              type="text/markdown"
              src="${this._privacyPolicyUrl}"
            ></script>
          </ld-markdown-element>
        </paper-dialog-scrollable>
        <div class="buttons">
          <paper-button @click="${() => this._onPrivacyDialogToggle()}">Close</paper-button>
        </div>
      </paper-dialog>

      <div id='sw-stripe'></div>
      <div id="sw-logo"></div>
      <div id="lifedata-logo"></div>

      <section class='ld-login-area'>

        ${_maintenanceAnnouncement}

        <slot></slot>

      </section>

      <footer>
        <ul class="flex">
          <li><a tabindex="-1" href="https://www.sw-machines.com/en/" target="_blank">SW Homepage</a></li>
          <li><a tabindex="-1" href="https://sw-machines.com/en/imprint/" target="_blank">Imprint</a></li>
          <li><a tabindex="-1" href="https://sw-machines.com/en/gtc/" target="_blank">GTCT / Standards</a>
          </li>
          <li><a tabindex="-1" @click="${() => this._onPrivacyDialogToggle()}">Privacy</a></li>
          <li><a tabindex="-1" href="mailto:lifedata@sw-machines.com">Contact Us</a></li>
        </ul>
      </footer>
    `;
  }

  // --------------------------------------------------------------------------------------
  protected _onLoginToastCloseClick() {
    const _toastElement = <PaperToastElement>this.shadowRoot!.querySelector('paper-toast#ld-login-toast');
    _toastElement.close();
  }

  // --------------------------------------------------------------------------------------
  protected _onPrivacyLanguageChange(e: Event) {
    // @ts-ignore
    const language: string = e.target!.dataset!.lng;
    // @ts-ignore
    this._privacyPolicyUrl = baseConfig.privacyPolicy.baseUrl + baseConfig.privacyPolicy.documentUrls[language.toLowerCase()];
  }

  // --------------------------------------------------------------------------------------
  protected _onPrivacyDialogToggle(): void {
    const _el = <PaperDialogElement>this.shadowRoot!.querySelector('#privacyPolicyDialog');
    if (_el.opened === true) {
      _el.close();
      return;
    }
    _el.open();
  }

  private setupListnerForTokenRequest() {
    const onMessageCallback = function(e: MessageEvent) {
      const platform = baseConfig.defaultRedirectUrl;
      const tokenKey = baseConfig.localstorage.token_key;

      if (e.origin !== platform) return;

      const {eventType} = e.data;

      if (eventType === "request-login-token") {
        const token = localStorage.getItem(tokenKey);
        const targetWindow = e.source as Window;
        targetWindow.postMessage({eventType, token}, platform);
        window.onmessage = null;
      }
    };
    window.onmessage = onMessageCallback
  }

}
